/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';

import { formatPreviewAccion, formatPreviewDetail } from '../csv-core/domain/csvUiCopy';
import { colors } from '../theme/public-api';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { CsvPreviewPagination } from './CsvPreviewPagination';
import { csvImportExportModalStyles as styles } from './csvImportExportModal.styles';
import {
  PREVIEW_PAGE_SIZES,
  plural,
  previewAccionTone,
  previewRowHasAviso,
} from './hooks/csvPreviewHelpers';
import type { PreviewCounts, PreviewPageSize, PreviewRowView } from './hooks/csvPreviewHelpers';

function PreviewAccionBadge({
  status,
  action,
  hasAviso,
  compact,
}: {
  status: string;
  action: string;
  hasAviso: boolean;
  compact: boolean;
}) {
  const tone = previewAccionTone(status, action, hasAviso);
  const label = formatPreviewAccion(status, action);
  return (
    <View
      style={[
        styles.previewBadge,
        compact && styles.previewBadgeCompact,
        tone === 'alta' && styles.previewBadgeAlta,
        tone === 'edicion' && styles.previewBadgeEdicion,
        tone === 'error' && styles.previewBadgeError,
        tone === 'igual' && styles.previewBadgeIgual,
        tone === 'aviso' && styles.previewBadgeAviso,
      ]}
      accessibilityLabel={`Acción: ${label}${hasAviso ? ', con aviso' : ''}`}
    >
      <Text
        style={[
          styles.previewBadgeText,
          tone === 'alta' && styles.previewBadgeTextAlta,
          tone === 'edicion' && styles.previewBadgeTextEdicion,
          tone === 'error' && styles.previewBadgeTextError,
          tone === 'igual' && styles.previewBadgeTextIgual,
          tone === 'aviso' && styles.previewBadgeTextAviso,
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
    </View>
  );
}

function formatExpandedDetail(row: PreviewRowView): string {
  const hint = row.message?.trim();
  if (!hint) return row.detail;
  if (row.status === 'skip') return row.detail;
  return `${row.detail} · ${hint}`;
}

function PreviewTable({ rows, compact }: { rows: PreviewRowView[]; compact: boolean }) {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  return (
    <View style={styles.previewTable}>
      <View style={styles.previewHeaderRow}>
        <Text style={[styles.previewCell, styles.previewCellFila]}>#</Text>
        <Text style={[styles.previewCell, styles.previewCellAccionCol]}>Acción</Text>
        <Text style={[styles.previewCell, styles.previewCellDetalle]}>Detalle</Text>
      </View>
      {rows.map((row) => {
        const hasAviso = previewRowHasAviso(row);
        const expanded = expandedRow === row.rowNumber;
        const canExpand = Boolean(row.message?.trim()) || row.detail.length > 36;
        return (
          <TouchableOpacity
            key={row.rowNumber}
            style={[styles.previewDataRow, expanded && styles.previewDataRowExpanded]}
            onPress={() => {
              if (!canExpand) return;
              setExpandedRow((cur) => (cur === row.rowNumber ? null : row.rowNumber));
            }}
            disabled={!canExpand}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            accessibilityHint={canExpand ? 'Ver el detalle completo' : undefined}
          >
            <Text style={[styles.previewCell, styles.previewCellFila]} numberOfLines={1}>
              {row.rowNumber}
            </Text>
            <View style={styles.previewCellAccionCol}>
              <PreviewAccionBadge
                status={row.status}
                action={row.action}
                hasAviso={hasAviso}
                compact={compact}
              />
            </View>
            <Text
              style={[
                styles.previewCell,
                styles.previewCellDetalle,
                styles.previewDetailMono,
                expanded && styles.previewDetailExpanded,
              ]}
              numberOfLines={expanded ? undefined : 1}
              ellipsizeMode="tail"
            >
              {expanded
                ? formatExpandedDetail(row)
                : formatPreviewDetail(row.status, row.detail, row.message)}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function PreviewStatsSummary({ counts }: { counts: PreviewCounts }) {
  const pills: Array<{
    key: string;
    value: number;
    label: string;
    tone: 'new' | 'edit' | 'error' | 'aviso';
  }> = [];
  if (counts.altas > 0) {
    pills.push({ key: 'altas', value: counts.altas, label: plural(counts.altas, 'alta', 'altas'), tone: 'new' });
  }
  if (counts.modificaciones > 0) {
    pills.push({
      key: 'mods',
      value: counts.modificaciones,
      label: plural(counts.modificaciones, 'edición', 'ediciones'),
      tone: 'edit',
    });
  }
  if (counts.avisos > 0) {
    pills.push({
      key: 'avisos',
      value: counts.avisos,
      label: plural(counts.avisos, 'aviso', 'avisos'),
      tone: 'aviso',
    });
  }
  if (counts.errores > 0) {
    pills.push({ key: 'err', value: counts.errores, label: plural(counts.errores, 'error', 'errores'), tone: 'error' });
  }

  if (pills.length === 0) {
    return (
      <View style={styles.statsWrap}>
        <Text style={styles.statsAllEqual}>Sin cambios para aplicar</Text>
        {counts.iguales > 0 ? (
          <Text style={styles.statsMuted}>
            {counts.iguales} {plural(counts.iguales, 'fila igual', 'filas iguales')} al catálogo
          </Text>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.statsWrap}>
      <View style={styles.statsPillRow}>
        {pills.map((p) => (
          <View
            key={p.key}
            style={[
              styles.statsPill,
              p.tone === 'new' && styles.statsPillNew,
              p.tone === 'aviso' && styles.statsPillAviso,
              p.tone === 'error' && styles.statsPillError,
            ]}
          >
            <Text
              style={[
                styles.statsPillValue,
                p.tone === 'new' && styles.statsPillValueNew,
                p.tone === 'aviso' && styles.statsPillValueAviso,
                p.tone === 'error' && styles.statsPillValueError,
              ]}
            >
              {p.value}
            </Text>
            <Text style={styles.statsPillLabel}>{p.label}</Text>
          </View>
        ))}
      </View>
      {counts.iguales > 0 ? (
        <Text style={styles.statsMuted}>
          {counts.iguales} {plural(counts.iguales, 'fila igual', 'filas iguales')} sin cambios
        </Text>
      ) : null}
    </View>
  );
}

type CsvImportPreviewStepProps = {
  fileName: string | null;
  previewStats: unknown;
  previewCounts: PreviewCounts;
  canConfirmImport: boolean;
  previewErrorsOnly: boolean;
  previewWarningsOnly: boolean;
  showEqualRows: boolean;
  previewTotalCount: number;
  previewPageIndex: number;
  previewPageSize: PreviewPageSize;
  pagedPreviewRows: PreviewRowView[];
  isCompactPreview: boolean;
  onTogglePreviewErrorsOnly: () => void;
  onTogglePreviewWarningsOnly: () => void;
  onToggleShowEqualRows: () => void;
  onPageSizeChange: (size: PreviewPageSize) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
};

export const CsvImportPreviewStep = ({
  fileName,
  previewStats,
  previewCounts,
  canConfirmImport,
  previewErrorsOnly,
  previewWarningsOnly,
  showEqualRows,
  previewTotalCount,
  previewPageIndex,
  previewPageSize,
  pagedPreviewRows,
  isCompactPreview,
  onTogglePreviewErrorsOnly,
  onTogglePreviewWarningsOnly,
  onToggleShowEqualRows,
  onPageSizeChange,
  onPreviousPage,
  onNextPage,
}: CsvImportPreviewStepProps) => (
  <>
    {fileName ? <Text style={styles.fileName}>Archivo: {fileName}</Text> : null}
    {previewStats ? <PreviewStatsSummary counts={previewCounts} /> : null}
    <Text style={styles.previewHint}>
      {canConfirmImport
        ? previewCounts.errores > 0
          ? `Las ${previewCounts.errores} con error no se importan; el resto sí.`
          : previewCounts.avisos > 0
            ? `Hay ${previewCounts.avisos} ${plural(previewCounts.avisos, 'aviso', 'avisos')}: la fila se aplica, pero un campo se ignora o se conserva.`
            : 'Revisá y confirmá los cambios. Cada fila se puede expandir para ver el detalle completo.'
        : 'Corregí el archivo o reexportá desde Vuelto para poder confirmar.'}
    </Text>
    <View style={styles.previewControls}>
      {previewCounts.errores > 0 ? (
        <TouchableOpacity style={styles.inlineLink} onPress={onTogglePreviewErrorsOnly} accessibilityRole="button">
          <Text style={styles.inlineLinkText}>
            {previewErrorsOnly ? 'Ver todo' : `Ver solo errores (${previewCounts.errores})`}
          </Text>
        </TouchableOpacity>
      ) : null}
      {previewCounts.avisos > 0 && !previewErrorsOnly ? (
        <TouchableOpacity
          style={styles.inlineLink}
          onPress={onTogglePreviewWarningsOnly}
          accessibilityRole="button"
        >
          <Text style={styles.inlineLinkText}>
            {previewWarningsOnly ? 'Ver todo' : `Ver solo avisos (${previewCounts.avisos})`}
          </Text>
        </TouchableOpacity>
      ) : null}
      {!previewErrorsOnly && !previewWarningsOnly && previewCounts.iguales > 0 ? (
        <TouchableOpacity style={styles.inlineLink} onPress={onToggleShowEqualRows} accessibilityRole="button">
          <Text style={styles.inlineLinkText}>
            {showEqualRows ? 'Ocultar iguales' : `Ver iguales (${previewCounts.iguales})`}
          </Text>
        </TouchableOpacity>
      ) : null}
    </View>
    {previewTotalCount === 0 ? (
      <Text style={styles.previewEmptyNote}>
        {previewErrorsOnly
          ? 'No hay filas con error.'
          : previewWarningsOnly
            ? 'No hay filas con aviso.'
            : 'Todas las filas son iguales al catálogo. No hay nada para aplicar.'}
      </Text>
    ) : null}
    <PreviewTable rows={pagedPreviewRows} compact={isCompactPreview} />
    <CsvPreviewPagination
      pageIndex={previewPageIndex}
      pageSize={previewPageSize}
      totalCount={previewTotalCount}
      pageSizeOptions={PREVIEW_PAGE_SIZES}
      onPageSizeChange={onPageSizeChange}
      onPrevious={onPreviousPage}
      onNext={onNextPage}
      compact={isCompactPreview}
    />
  </>
);

type CsvImportPreviewActionsProps = {
  isCompactPreview: boolean;
  committing: boolean;
  canConfirmImport: boolean;
  onBack: () => void;
  onConfirmImport: () => void;
};

export const CsvImportPreviewActions = ({
  isCompactPreview,
  committing,
  canConfirmImport,
  onBack,
  onConfirmImport,
}: CsvImportPreviewActionsProps) => (
  <View style={[styles.footerActions, styles.actionRow, isCompactPreview && styles.actionRowStacked]}>
    <TouchableOpacity
      style={[posSharedUi.secondaryButton, styles.actionBtn, isCompactPreview && styles.actionBtnStacked]}
      onPress={onBack}
      disabled={committing}
    >
      <Text style={posSharedUi.secondaryButtonText}>Volver</Text>
    </TouchableOpacity>
    {canConfirmImport ? (
      <TouchableOpacity
        style={[posSharedUi.primaryButton, styles.actionBtn, isCompactPreview && styles.actionBtnStacked]}
        onPress={onConfirmImport}
        disabled={committing}
      >
        {committing ? (
          <ActivityIndicator color={colors.background} />
        ) : (
          <Text style={posSharedUi.primaryButtonText}>
            {isCompactPreview ? 'Confirmar' : 'Confirmar importación'}
          </Text>
        )}
      </TouchableOpacity>
    ) : null}
  </View>
);
